import { getLevelFromXP } from '@blind-kings/game-core';
import ProfileAvatar from '../../Components/ProfileAvatar';
import BkPanel from '../../Components/ui/BkPanel';
import { tierLabel } from '../../profile-frames/registry';

export type MatchOpponent = {
  displayName?: string;
  avatarId?: string;
  displayFrameId?: string;
  xp?: number;
  wins?: number;
  losses?: number;
  level?: number;
  elo?: number;
  tier?: string;
  rankedWins?: number;
  rankedLosses?: number;
};

export type MatchRevealProps = {
  modeLabel: string;
  you: MatchOpponent;
  opponent: MatchOpponent;
  secondsLeft: number;
};

function SeatCard({
  label,
  seat,
  highlight,
  showRanked,
}: {
  label: string;
  seat: MatchOpponent;
  highlight?: boolean;
  showRanked?: boolean;
}) {
  const level =
    seat.level ??
    (seat.xp !== undefined ? getLevelFromXP(Number(seat.xp) || 0) : undefined);
  const wins = Number(
    showRanked ? (seat.rankedWins ?? seat.wins ?? 0) : (seat.wins ?? 0)
  );
  const losses = Number(
    showRanked ? (seat.rankedLosses ?? seat.losses ?? 0) : (seat.losses ?? 0)
  );
  const name = seat.displayName?.trim() || 'Unknown';
  const tier = seat.tier;
  const elo = seat.elo;

  return (
    <div
      className={[
        'flex flex-1 flex-col items-center gap-3 border px-4 py-5',
        highlight
          ? 'border-bk-gold bg-bk-gold/10 shadow-[0_0_24px_color-mix(in_srgb,var(--color-bk-gold)_25%,transparent)]'
          : 'border-bk-border bg-bk-bg/80',
      ].join(' ')}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-bk-muted">
        {label}
      </p>
      <ProfileAvatar
        avatarId={seat.avatarId}
        frameId={seat.displayFrameId}
        size="lg"
        className="!h-16 !w-16"
      />
      <div className="text-center">
        <div className="truncate text-lg font-extrabold tracking-wide text-bk-text">
          {name}
        </div>
        <div className="mt-1 text-sm font-bold uppercase tracking-wider text-bk-gold-bright">
          {level !== undefined ? `Level ${level}` : 'Level —'}
        </div>
        <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-bk-muted">
          {showRanked
            ? `${tierLabel(tier)}${elo != null ? ` · ${elo}` : ''}`
            : 'Casual'}
        </div>
        <div className="mt-1 text-xs uppercase tracking-wide text-bk-muted">
          Record {wins}–{losses}
        </div>
      </div>
    </div>
  );
}

export default function MatchReveal({
  modeLabel,
  you,
  opponent,
  secondsLeft,
}: MatchRevealProps) {
  const showRanked = modeLabel.toLowerCase().includes('ranked');
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-4 py-12">
      <BkPanel className="w-full max-w-2xl space-y-8 text-center animate-bk-pulse-soft">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-bk-gold-dim">
            Match found · {modeLabel}
          </p>
          <h1 className="font-display text-3xl font-extrabold tracking-wide text-bk-text sm:text-4xl">
            Versus
          </h1>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <SeatCard label="You" seat={you} highlight showRanked={showRanked} />
          <SeatCard label="Rival" seat={opponent} showRanked={showRanked} />
        </div>
        <p className="text-sm text-bk-muted">
          Starting in{' '}
          <span className="font-extrabold tabular-nums text-bk-gold">
            {secondsLeft}
          </span>
        </p>
      </BkPanel>
    </div>
  );
}
